
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import Editor from '@monaco-editor/react'
import SpreadsheetUpload from '../components/SpreadsheetUpload';
import { submitJob } from '../api';

const luaScriptTemplate = `-- ========== COLUMN PREVIEW ==========
-- Sheet A Columns (first two)
local a_col1 = sheetA[1][1]
local a_col2 = sheetA[1][2]

-- Sheet B Columns (first two)
local b_col1 = sheetB[1][1]
local b_col2 = sheetB[1][2]

-- ========== HELPER FUNCTIONS ==========

-- Add a new column to the end of a sheet
function addColumn(sheet, columnName, values)
    for i, row in ipairs(sheet) do
        if i == 1 then
            table.insert(row, columnName)
        else
            table.insert(row, values[i - 1])
        end
    end
end

-- Compare Sheet A's colAIndex with Sheet B's colBIndex
function compareColumns(sheetA, colAIndex, sheetAName, sheetB, colBIndex, sheetBName)
    local comparisonA = {}
    local comparisonB = {}
    local colAHeader = sheetA[1][colAIndex]
    local colBHeader = sheetB[1][colBIndex]
    
    -- Compare Sheet A's colAIndex against Sheet B's colBIndex
    for i = 2, #sheetA do
        local matchFoundA = "False"
        for j = 2, #sheetB do
            if sheetA[i][colAIndex] == sheetB[j][colBIndex] then
                matchFoundA = "True"
                break
            end
        end
        table.insert(comparisonA, matchFoundA)
    end
    
    -- Compare Sheet B's colBIndex against Sheet A's colAIndex
    for i = 2, #sheetB do
        local matchFoundB = "False"
        for j = 2, #sheetA do
            if sheetB[i][colBIndex] == sheetA[j][colAIndex] then
                matchFoundB = "True"
                break
            end
        end
        table.insert(comparisonB, matchFoundB)
    end
    
    -- Add the results to the sheets
    addColumn(sheetA, colAHeader .. " found in " .. sheetBName .. "?", comparisonA)
    addColumn(sheetB, colBHeader .. " found in " .. sheetAName .. "?", comparisonB)
end

-- ========== USER LOGIC ==========
function userLogic(sheetA, sheetB)
    -- Example usage: compare two columns from Sheet A and Sheet B
    compareColumns(sheetA, 2, "Sheet A", sheetB, 1, "Sheet B")
end

-- ========== EXECUTE ==========
userLogic(sheetA, sheetB)
`;
const debtorsComparisonTemplate = `-- === PREREQUISITES ===
-- The first row of the CRM report should contain the column headers. [Delete all the rows before the cloumn headers.]
-- The CRM report should be uploaded second and the Insight Report should be uploaded first.

local months = { Jan=1, Feb=2, Mar=3, Apr=4, May=5, Jun=6, Jul=7, Aug=8, Sep=9, Oct=10, Nov=11, Dec=12 }

function parseSheetADate(str)
  local day, monStr, year = str:match("(%d+)%-(%a+)%-(%d+)")
  local month = months[monStr]
  if day and month and year then
    return os.time{year=tonumber(year), month=month, day=tonumber(day)}
  end
end

function parseSheetBDate(str)
  local year, day, month = str:match("(%d+)%-(%d+)%-(%d+)")
  if year and day and month then
    return os.time{year=tonumber(year), month=tonumber(month), day=tonumber(day)}
  end
end

function parseDDMMYYYYDate(str)
  local day, month, year = str:match("(%d+)%/(%d+)%/(%d+)")
  if day and month and year then
    return os.time{year=tonumber(year), month=tonumber(month), day=tonumber(day)}
  end
end

function formatDate(timestamp)
  return timestamp and os.date("%d-%b-%Y", timestamp) or "-"
end

function getOverdueCategory(days)
  if days <= 14 then return "0-14"
  elseif days <= 30 then return "15-30"
  elseif days <= 60 then return "31-60"
  elseif days <= 90 then return "61-90"
  elseif days <= 120 then return "91-120"
  else return ">120" end
end

function getToday()
  local now = os.date("*t")
  return os.time{year=now.year, month=now.month, day=now.day}
end

function parseNumber(str)
  if not str or str == "" then return nil end
  str = str:gsub(",", ""):gsub("%$", ""):gsub(" ", "")
  if str:match("^%(%d+%.?%d*%)$") then
    str = "-" .. str:match("%(([%d%.]+)%)")
  end
  return tonumber(str)
end

-- === STEP 1: Clean and transform Sheet A ===
local headerFound = false
local newSheetA = {}
for i, row in ipairs(sheetA) do
  if not headerFound and row[1] == "Invoice No." then
    table.insert(row, "Overdue Category")
    newSheetA[1] = row
    headerFound = true
  elseif headerFound then
    local invoiceVal = row[7]
    if invoiceVal and invoiceVal ~= "-" and invoiceVal ~= "" then
      local rawDate = row[2] or ""
      local invoiceDate = parseDDMMYYYYDate(rawDate) or parseSheetADate(rawDate)
      local days = invoiceDate and math.floor((getToday() - invoiceDate) / (24 * 3600))
      local category = days and getOverdueCategory(days) or "-"
      table.insert(row, category)
      table.insert(newSheetA, row)
    end
  end
end
sheetA = newSheetA

-- === STEP 2: Define comparison ===
local comparisonPairs = {
  {aIdx=1, bIdx=4, aCol="Invoice No.", bCol="Invoice Number"},
  {aIdx=2, bIdx=5, aCol="Invoice Date", bCol="Invoice Date"},
  {aIdx=9, bIdx=6, aCol="Invoice Amount", bCol="Invoice Total"},
  {aIdx=10, bIdx=7, aCol="Balance", bCol="Invoice Balance"},
  {aIdx=16, bIdx=9, aCol="Funding Status", bCol="Funding Status"},
  {aIdx=17, bIdx=11, aCol="Overdue Category", bCol="Overdue Category"},
}

-- Add comparison headers
for _, pair in ipairs(comparisonPairs) do
  table.insert(sheetA[1], pair.aCol .. " found in Debtor+Comparison+Report?")
  table.insert(sheetB[1], pair.bCol .. " found in Debtor_Aging_Report?")
end

-- === STEP 3: Index for lookup ===
local bIndex, aIndex = {}, {}
for i = 2, #sheetB do
  local key = sheetB[i][4]
  if key then bIndex[key] = sheetB[i] end
end
for i = 2, #sheetA do
  local key = sheetA[i][1]
  if key then aIndex[key] = sheetA[i] end
end

-- === STEP 4: A → B comparison ===
for i = 2, #sheetA do
  local rowA, matchB = sheetA[i], bIndex[sheetA[i][1]]
  for _, pair in ipairs(comparisonPairs) do
    local result = "false"
    if matchB then
      if pair.aCol == "Invoice No." then
        result = "true"
      else
        local va, vb = rowA[pair.aIdx] or "", matchB[pair.bIdx] or ""
        if pair.aCol == "Invoice Date" then
          local da = parseSheetADate(va) or parseDDMMYYYYDate(va)
          local db = parseSheetBDate(vb) or parseDDMMYYYYDate(vb)
          if da and db and da == db then result = "true" end
        elseif pair.aCol == "Invoice Amount" or pair.aCol == "Balance" then
          local numA = parseNumber(va)
          local numB = parseNumber(vb)
          if numA and numB and math.abs(numA - numB) < 0.01 then result = "true" end
        else
          if pair.aCol == "Overdue Category" then
            va = va:gsub("%s+", "")
            vb = vb:gsub("%s+", "")
          end
          if va == vb then result = "true" end
        end
      end
    end
    table.insert(rowA, result)
  end
end

-- === STEP 5: B → A comparison + converted date ===
for i = 2, #sheetB do
  local rowB, matchA = sheetB[i], aIndex[sheetB[i][4]]
  for _, pair in ipairs(comparisonPairs) do
    local result = "false"
    if matchA then
      if pair.bCol == "Invoice Number" then
        result = "true"
      else
        local vb, va = rowB[pair.bIdx] or "", matchA[pair.aIdx] or ""
        if pair.bCol == "Invoice Date" then
          local db = parseSheetBDate(vb) or parseDDMMYYYYDate(vb)
          local da = parseSheetADate(va) or parseDDMMYYYYDate(va)
          if da and db and da == db then result = "true" end
        elseif pair.bCol == "Invoice Total" or pair.bCol == "Invoice Balance" then
          local numA = parseNumber(va)
          local numB = parseNumber(vb)
          if numA and numB and math.abs(numA - numB) < 0.01 then result = "true" end
        else
          if pair.bCol == "Overdue Category" then
            va = va:gsub("%s+", "")
            vb = vb:gsub("%s+", "")
          end
          if vb == va then result = "true" end
        end
      end
    end
    table.insert(rowB, result)
  end
end`;

const CustomComparison = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState<'single' | 'dual' | null>(null);
  const [sheetA, setSheetA] = useState<File | null>(null);
  const [sheetB, setSheetB] = useState<File | null>(null);
  const [script, setScript] = useState<string>(luaScriptTemplate);
  const [previewA, setPreviewA] = useState<string[][]>([]);
  const [previewB, setPreviewB] = useState<string[][]>([]);
  const [error, setError] = useState<string>('');
  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const getFileBaseName = (filename: string) =>
    filename.replace(/\.[^/.]+$/, '');

  const toTitleCase = (str: string) =>
    str.replace(/_/g, ' ').replace(/\w\S*/g, txt => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

  const handleUpload = async (a: File | null, b: File | null) => {
    setSheetA(a);
    setSheetB(b);
    setError('');
    if (!a || !b) return;

    const formData = new FormData();
    formData.append('sheetA', a);
    formData.append('sheetB', b);

    try {
      const res = await fetch('/api/columns', {
        method: 'POST',
        body: formData,
      });

      const data = await res.json();
      console.log('Full response from /api/columns:', data);

      const sheetAColumns = data.sheetAColumns || [];
      const sheetBColumns = data.sheetBColumns || [];
      const sheetAData = data.sheetAData || [];
      const sheetBData = data.sheetBData || [];

      const fullSheetA = [sheetAColumns, ...sheetAData];
      const fullSheetB = [sheetBColumns, ...sheetBData];

      setPreviewA(fullSheetA.slice(0, 6));
      setPreviewB(fullSheetB.slice(0, 6));
    } catch (err) {
      console.error('Preview error:', err);
      setError('There was an error while loading the columns or data. Please try again.');
    }
  };

  const handleRun = async () => {
    if (!script || !sheetA || !sheetB) return;

    try {
      await submitJob(
        '/api/compare',
        { sheetA, sheetB },
        { script },
        'comparison_result.xlsx',
      );
      console.log('Comparison file downloaded successfully.');
    } catch (err) {
      console.error('Error during Lua execution:', err);
      setError('There was an error while running the comparison.');
    }
  };

  const handleDebtorsComparisonClick = () => {
    console.log("Debtors Comparison button clicked");
    setScript(debtorsComparisonTemplate);
  };

  const renderTable = (data: string[][], title: string) => (
    <Box sx={{ marginTop: '30px' }}>
      <Typography variant="h6" sx={{ fontWeight: 'medium', marginBottom: '10px' }}>
        {title}
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {data[0]?.map((cell, idx) => (
                <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(1).map((row, i) => (
              <TableRow key={i}>
                {row.map((cell, j) => (
                  <TableCell key={j}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  const sheetATitle = sheetA ? `${toTitleCase(getFileBaseName(sheetA.name))} PREVIEW` : '';
  const sheetBTitle = sheetB ? `${toTitleCase(getFileBaseName(sheetB.name))} PREVIEW` : '';

  return (
    <Container maxWidth="lg" sx={{ textAlign: 'center', paddingTop: '50px' }}>
      <Box sx={{ backgroundColor: '#183B4E', color: 'white', padding: '20px', borderRadius: '8px' }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
          Custom Comparison
        </Typography>
        <Typography variant="h6" sx={{ fontStyle: 'italic', marginBottom: '40px' }}>
          Write your own code to compare sheets.
        </Typography>
      </Box>

      {/* Step 1: Choose Analysis Mode */}
      {!mode && (
        <Paper elevation={6} sx={{ padding: '40px', borderRadius: '8px', marginTop: '40px' }}>
          <Typography variant="h5" sx={{ marginBottom: '20px' }}>
            SELECT ANALYSIS MODE
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '20px' }}>
          <Button variant="contained" onClick={() => navigate('/single-analysis')}>
            Single Spreadsheet Analysis
          </Button>
            <Button variant="contained" color="primary" onClick={() => setMode('dual')}>
              Dual Spreadsheet Comparison
            </Button>
          </Box>
        </Paper>
      )}

      {/* Step 2: Dual Spreadsheet Comparison */}
      {mode === 'dual' && (
        <Paper elevation={6} sx={{ padding: '30px', borderRadius: '8px', marginTop: '30px', backgroundColor: '#f5f5f5' }}>
          <SpreadsheetUpload onUpload={handleUpload} />

          {error && <Typography color="error">{error}</Typography>}

          {previewA.length > 0 && renderTable(previewA, sheetATitle)}
          {previewB.length > 0 && renderTable(previewB, sheetBTitle)}

          {sheetA && sheetB && (
            <>
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    position: 'absolute',
                    top: '-5px',
                    right: '10px',
                    display: 'flex',
                    gap: '10px',
                  }}
                >
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#FF5733',
                      color: 'white',
                    }}
                    onClick={() => setOpenDialog(true)}
                  >
                    Explain Script
                  </Button>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: '#FF5733',
                      color: 'white',
                    }}
                    onClick={handleDebtorsComparisonClick}
                  >
                    Debtors Comparison
                  </Button>
                </Box>

                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'medium', marginTop: '30px', marginBottom: '10px' }}
                >
                  CUSTOM SCRIPT
                </Typography>
              </Box>

              <Editor
                height="300px"
                language="lua"
                value={script}
                onChange={(value) => setScript(value ?? '')}
                theme="vs-dark"
                options={{
                  fontSize: 14,
                  minimap: { enabled: false },
                  scrollBeyondLastLine: false,
                  fontLigatures: true,
                  fontFamily: '"Fira Code", monospace',
                  wordWrap: 'on',
                  tabSize: 2
                }}
              />

              <Button
                variant="contained"
                sx={{ marginTop: '30px', backgroundColor: '#27548A' }}
                onClick={handleRun}
                disabled={!sheetA || !sheetB || !script}
              >
                Run Comparison
              </Button>
            </>
          )}
        </Paper>
      )}

      {/* Dialog for "Explain Script" */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Explanation of Lua Script</DialogTitle>
        <DialogContent>
          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '15px' }}>
            Execution Flow
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
            <strong>1. Execution starts at</strong> <code>userLogic(sheetA, sheetB)</code><br />
            This is where the user writes their comparison logic.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
            <strong>2. Inside <code>userLogic</code>, helper functions like <code>compareColumns</code> are called:</strong><br />
            <code>compareColumns</code> takes indices of columns from both sheets, compares values, and writes new result columns at the end.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
            <strong>3. What <code>compareColumns</code> does:</strong>
            <ul>
              <li><strong>Sheet A vs. Sheet B check</strong>: Compares the values from a column in Sheet A with the corresponding column in Sheet B.</li>
              <li><strong>Sheet B vs. Sheet A check</strong>: Compares the values from a column in Sheet B with the corresponding column in Sheet A.</li>
              <li><strong>Results</strong>: "True" or "False" are collected and added to the sheets using the <code>addColumn</code> helper function.</li>
            </ul>
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '15px' }}>
            <strong>4. The new columns appear at the end of the respective sheets with the format:</strong><br />
            <code>&lt;Original Column Name&gt; found in &lt;Other Sheet Name&gt;?</code>
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 'bold', marginBottom: '15px' }}>
            How to Customize
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
            <strong>1. Identify the columns to compare:</strong><br />
            Use <code>sheetA[1][x]</code> or <code>sheetB[1][y]</code> to preview columns and decide which ones to compare.
          </Typography>
          <Typography variant="body1" sx={{ marginBottom: '10px' }}>
            <strong>2. Write your logic inside <code>userLogic(sheetA, sheetB)</code>:</strong><br />
            Example: Compare "Client Name" from Sheet A (column 1) with "Client Debtor Name" from Sheet B (column 1):
            <pre><code>compareColumns(sheetA, 1, "CRM Sheet", sheetB, 1, "Finance Sheet")</code></pre>
          </Typography>
          <Typography variant="body1">
            <strong>3. You can call <code>compareColumns</code> multiple times to compare more pairs or add your own functions to perform custom analysis.</strong>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default CustomComparison;